import { useMemo } from 'react';

import { createNoResultList } from '../filter/list-filter.helpers';

import { ListBuilder } from './list-item.builder';

type KeysMatching<T, V> = {
  [K in keyof T]-?: T[K] extends V ? K : never;
}[keyof T];

/**
 * Generic list builder hook to convert array of objects to stateful list
 * @param items Array of objects to be converted into a stateful list
 * @param labelKey object key to be used as the label for each list item
 * @param valueKey object key to be used as the value for each list item
 * @returns
 */
export const useListBuilder = <T extends object>(
  items: T[],
  labelKey: KeysMatching<T, string | undefined | null> = 'value' as KeysMatching<
    T,
    string | undefined | null
  >,
  valueKey: KeysMatching<T, string | number | undefined | null> = 'key' as KeysMatching<
    T,
    string | number | undefined | null
  >,
) => {
  return useMemo(() => {
    const validItems = Array.isArray(items)
      ? items.filter((item) => item[valueKey] != null && item[labelKey] != null)
      : [];
    if (!validItems.length) return createNoResultList({ label: 'No results' });

    const builder = ListBuilder.create();

    builder.addGroup((group) => {
      validItems.forEach((item) => {
        const value = item[valueKey] as unknown as string | number;
        const label = item[labelKey] as unknown as string;
        group.addItem(value, label);
      });

      return group;
    });

    return builder.build();
  }, [items, labelKey, valueKey]);
};
